import { useState } from "react";

export interface VisitListingSummary {
  listing_id: string;
  address?: string;
  town?: string;
  status?: string;
  list_price?: number | null;
  sale_price?: number | null;
  sqft?: number | null;
  beds?: number | null;
  baths?: number | null;
  match_score?: number | null;
  thumbnail?: string;
  url?: string;
}

// Quick questions asked after a showing. Keep keys and values in sync with
// the Visit model in backend/lib/schemas.py, which rejects anything else.
export interface VisitAnswers {
  layout_flow?: number | null;
  bedroom_sizes?: "too_small" | "fine" | "generous" | null;
  primary_suite?: "doesnt_work" | "fine" | "great" | null;
  real_bedrooms?: number | null;
  kitchen_plan?: "keep" | "refresh" | "renovate" | null;
  maintenance?: "low" | "moderate" | "high" | null;
  street_feel?: "quiet" | "fine" | "noisy" | null;
  would_offer?: "yes" | "maybe" | "no" | null;
  max_price?: number | null;
  dealbreaker?: string;
  delight?: string;
}

const CHOICE_QUESTIONS: { key: keyof VisitAnswers; label: string; options: [string, string][] }[] = [
  { key: "bedroom_sizes", label: "Bedroom sizes", options: [["too_small", "Too small"], ["fine", "Fine"], ["generous", "Generous"]] },
  { key: "primary_suite", label: "Primary suite", options: [["doesnt_work", "Doesn't work"], ["fine", "Fine"], ["great", "Great"]] },
  { key: "kitchen_plan", label: "Kitchen", options: [["keep", "Keep as is"], ["refresh", "Refresh"], ["renovate", "Renovate"]] },
  { key: "maintenance", label: "Upkeep (lot, pool, yard)", options: [["low", "Low"], ["moderate", "Moderate"], ["high", "High"]] },
  { key: "street_feel", label: "Street", options: [["quiet", "Quiet"], ["fine", "Fine"], ["noisy", "Noisy"]] },
  { key: "would_offer", label: "Would we offer?", options: [["yes", "Yes"], ["maybe", "Maybe"], ["no", "No"]] },
];

export interface Visit extends VisitAnswers {
  visit_id: string;
  listing_id: string;
  visited_on: string;
  rating: number | null;
  notes: string;
  created_at: string;
  updated_at: string;
  listing?: VisitListingSummary | null;
  offer?: Offer | null;
}

export interface VisitDraft extends VisitAnswers {
  listing_id: string;
  visited_on: string;
  rating: number | null;
  notes: string;
}

// Keep in sync with OFFER_STATUSES in backend/lib/schemas.py — the API rejects
// anything else.
export const OFFER_STATUSES = [
  { key: "submitted", label: "Submitted", color: "var(--warn)" },
  { key: "countered", label: "Countered", color: "var(--warn)" },
  { key: "accepted", label: "Accepted", color: "var(--good)" },
  { key: "rejected", label: "Rejected", color: "var(--bad)" },
  { key: "withdrawn", label: "Withdrawn", color: "var(--muted)" },
  { key: "expired", label: "Expired", color: "var(--muted)" },
] as const;

export type OfferStatus = (typeof OFFER_STATUSES)[number]["key"];

export interface Offer {
  listing_id: string;
  offered_on: string;
  amount: number | null;
  status: OfferStatus;
  notes: string;
  created_at: string;
  updated_at: string;
  listing?: VisitListingSummary | null;
}

export interface OfferDraft {
  offered_on: string;
  amount: number | null;
  status: OfferStatus;
  notes: string;
}

interface ListingOption {
  listing_id: string;
  address?: string;
  town?: string;
  status?: string;
}

export function todayIso(): string {
  // Local date, not UTC — a 9pm write-up shouldn't land on tomorrow.
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function VisitForm({
  listings,
  initial,
  lockedListingId,
  submitting,
  error,
  onSubmit,
  onCancel,
}: {
  listings: ListingOption[];
  initial?: Visit;
  /** Set when logging from a listing page — hides the picker. */
  lockedListingId?: string;
  submitting: boolean;
  error: string | null;
  onSubmit: (draft: VisitDraft) => void;
  onCancel: () => void;
}) {
  const [listingId, setListingId] = useState(initial?.listing_id ?? lockedListingId ?? "");
  const [visitedOn, setVisitedOn] = useState(initial?.visited_on ?? todayIso());
  const [rating, setRating] = useState<number | null>(initial?.rating ?? null);
  const [notes, setNotes] = useState(initial?.notes ?? "");
  const [answers, setAnswers] = useState<VisitAnswers>(() => {
    const a: VisitAnswers = {};
    for (const key of ANSWER_KEYS) (a as Record<string, unknown>)[key] = initial?.[key] ?? null;
    return { ...a, dealbreaker: initial?.dealbreaker ?? "", delight: initial?.delight ?? "" };
  });
  const [maxPrice, setMaxPrice] = useState(initial?.max_price != null ? String(initial.max_price) : "");
  const set = <K extends keyof VisitAnswers>(key: K, value: VisitAnswers[K]) =>
    setAnswers((prev) => ({ ...prev, [key]: value }));

  // Accept "1,250,000" and "$1250000" — the API wants a bare integer.
  const parsedMax = maxPrice.trim() ? Number(maxPrice.replace(/[$,\s]/g, "")) : null;
  const maxInvalid = parsedMax != null && (!Number.isInteger(parsedMax) || parsedMax <= 0);
  const canSubmit = !!listingId && !!visitedOn && !submitting && !maxInvalid;

  return (
    <div style={{ display: "grid", gap: 14 }}>
      {!lockedListingId && (
        <Field label="Listing">
          <select value={listingId} onChange={(e) => setListingId(e.target.value)}>
            <option value="">Select a listing…</option>
            {listings.map((l) => (
              <option key={l.listing_id} value={l.listing_id}>
                {l.address ?? l.listing_id}
                {l.town ? ` — ${l.town}` : ""}
                {l.status && l.status !== "active" ? ` (${l.status})` : ""}
              </option>
            ))}
          </select>
        </Field>
      )}

      <div className="row" style={{ gap: 24, alignItems: "flex-end" }}>
        <Field label="Visit date">
          <input type="date" value={visitedOn} onChange={(e) => setVisitedOn(e.target.value)} />
        </Field>
        <Field label="Rating">
          <StarPicker value={rating} onChange={setRating} />
        </Field>
      </div>

      <fieldset style={{ border: "1px solid var(--border)", borderRadius: 6, padding: "10px 12px", margin: 0 }}>
        <legend className="muted" style={{ fontSize: 11, padding: "0 4px" }}>
          Quick questions (optional — these teach the app what you care about)
        </legend>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))", gap: 10 }}>
          <Field label="Layout flow">
            <select
              value={answers.layout_flow ?? ""}
              onChange={(e) => set("layout_flow", e.target.value ? Number(e.target.value) : null)}
            >
              <option value="">—</option>
              <option value="1">1 · Doesn't work</option>
              <option value="2">2</option>
              <option value="3">3 · OK</option>
              <option value="4">4</option>
              <option value="5">5 · Flows well</option>
            </select>
          </Field>
          {CHOICE_QUESTIONS.map((q) => (
            <Field key={q.key} label={q.label}>
              <select
                value={(answers[q.key] as string | null | undefined) ?? ""}
                onChange={(e) => set(q.key, (e.target.value || null) as never)}
              >
                <option value="">—</option>
                {q.options.map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </Field>
          ))}
          <Field label="Real bedrooms (bedroom level)">
            <input
              type="number"
              min={0}
              max={10}
              value={answers.real_bedrooms ?? ""}
              onChange={(e) => set("real_bedrooms", e.target.value === "" ? null : Number(e.target.value))}
              style={{ width: 80 }}
            />
          </Field>
          <Field label="Most we'd pay">
            <input
              value={maxPrice}
              onChange={(e) => setMaxPrice(e.target.value)}
              placeholder="1250000"
              inputMode="numeric"
              style={{ width: 130 }}
            />
          </Field>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 10 }}>
          <Field label="Biggest dealbreaker">
            <input
              value={answers.dealbreaker ?? ""}
              maxLength={300}
              onChange={(e) => set("dealbreaker", e.target.value)}
              placeholder="Tiny secondary bedrooms"
            />
          </Field>
          <Field label="Best thing about it">
            <input
              value={answers.delight ?? ""}
              maxLength={300}
              onChange={(e) => set("delight", e.target.value)}
              placeholder="Open kitchen onto the yard"
            />
          </Field>
        </div>
      </fieldset>

      <Field label="Notes">
        <textarea
          rows={6}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Smells, noise, natural light, what the neighbors said, what you'd change…"
        />
      </Field>

      {maxInvalid && (
        <p className="tag-error" style={{ margin: 0 }}>
          "Most we'd pay" must be a whole dollar amount.
        </p>
      )}
      {error && <p className="tag-error" style={{ margin: 0 }}>{error}</p>}

      <div className="row">
        <button
          onClick={() =>
            canSubmit &&
            onSubmit({
              ...answers,
              max_price: parsedMax,
              dealbreaker: (answers.dealbreaker ?? "").trim(),
              delight: (answers.delight ?? "").trim(),
              listing_id: listingId,
              visited_on: visitedOn,
              rating,
              notes: notes.trim(),
            })
          }
          disabled={!canSubmit}
        >
          {submitting ? "Saving…" : initial ? "Save changes" : "Save visit"}
        </button>
        <SecondaryButton onClick={onCancel}>Cancel</SecondaryButton>
        {!listingId && !lockedListingId && (
          <span className="muted" style={{ fontSize: 12 }}>
            Pick a listing to save.
          </span>
        )}
      </div>
    </div>
  );
}

const ANSWER_KEYS = [
  "layout_flow", "bedroom_sizes", "primary_suite", "real_bedrooms", "kitchen_plan",
  "maintenance", "street_feel", "would_offer", "max_price",
] as const;

/** Answered quick questions as compact chips; renders nothing if none. */
export function VisitAnswerChips({ visit }: { visit: Visit }) {
  const chips: string[] = [];
  if (visit.layout_flow != null) chips.push(`Layout ${visit.layout_flow}/5`);
  for (const q of CHOICE_QUESTIONS) {
    const value = visit[q.key];
    const option = q.options.find(([v]) => v === value);
    if (option) chips.push(`${q.label}: ${option[1].toLowerCase()}`);
  }
  if (visit.real_bedrooms != null) chips.push(`${visit.real_bedrooms} real bedrooms`);
  if (visit.max_price != null) chips.push(`Most we'd pay: $${visit.max_price.toLocaleString()}`);
  const lines = [
    visit.dealbreaker ? `Dealbreaker: ${visit.dealbreaker}` : "",
    visit.delight ? `Best thing: ${visit.delight}` : "",
  ].filter(Boolean);
  if (!chips.length && !lines.length) return null;
  return (
    <div style={{ marginTop: 8 }}>
      {chips.length > 0 && (
        <div className="row" style={{ gap: 6, flexWrap: "wrap" }}>
          {chips.map((c) => (
            <span
              key={c}
              style={{ fontSize: 11, border: "1px solid var(--border)", borderRadius: 12, padding: "2px 8px" }}
            >
              {c}
            </span>
          ))}
        </div>
      )}
      {lines.map((l) => (
        <p key={l} className="muted" style={{ margin: "6px 0 0", fontSize: 13 }}>
          {l}
        </p>
      ))}
    </div>
  );
}

export function OfferForm({
  initial,
  submitting,
  error,
  onSubmit,
  onCancel,
}: {
  initial?: Offer | null;
  submitting: boolean;
  error: string | null;
  onSubmit: (draft: OfferDraft) => void;
  onCancel: () => void;
}) {
  const [offeredOn, setOfferedOn] = useState(initial?.offered_on ?? todayIso());
  const [amount, setAmount] = useState(initial?.amount != null ? String(initial.amount) : "");
  const [status, setStatus] = useState<OfferStatus>(initial?.status ?? "submitted");
  const [notes, setNotes] = useState(initial?.notes ?? "");

  // Accept "1,050,000" and "$1050000" — the API wants a bare integer.
  const parsedAmount = amount.trim() ? Number(amount.replace(/[$,\s]/g, "")) : null;
  const amountInvalid = parsedAmount != null && (!Number.isFinite(parsedAmount) || parsedAmount <= 0);
  const canSubmit = !!offeredOn && !amountInvalid && !submitting;

  return (
    <div style={{ display: "grid", gap: 14 }}>
      <div className="row" style={{ gap: 20, alignItems: "flex-end" }}>
        <Field label="Offer date">
          <input type="date" value={offeredOn} onChange={(e) => setOfferedOn(e.target.value)} />
        </Field>
        <Field label="Amount">
          <input
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="950000"
            inputMode="numeric"
            style={{ width: 140 }}
          />
        </Field>
        <Field label="Status">
          <select value={status} onChange={(e) => setStatus(e.target.value as OfferStatus)}>
            {OFFER_STATUSES.map((s) => (
              <option key={s.key} value={s.key}>
                {s.label}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <Field label="Notes">
        <textarea
          rows={4}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Terms, contingencies, what the seller came back with…"
        />
      </Field>

      {amountInvalid && (
        <p className="tag-error" style={{ margin: 0 }}>
          Amount must be a positive number.
        </p>
      )}
      {error && <p className="tag-error" style={{ margin: 0 }}>{error}</p>}

      <div className="row">
        <button
          onClick={() =>
            canSubmit &&
            onSubmit({
              offered_on: offeredOn,
              amount: parsedAmount,
              status,
              notes: notes.trim(),
            })
          }
          disabled={!canSubmit}
        >
          {submitting ? "Saving…" : initial ? "Save changes" : "Record offer"}
        </button>
        <SecondaryButton onClick={onCancel}>Cancel</SecondaryButton>
      </div>
    </div>
  );
}

export function OfferBadge({ offer }: { offer: Offer }) {
  const meta = OFFER_STATUSES.find((s) => s.key === offer.status);
  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 600,
        border: `1px solid ${meta?.color ?? "var(--border)"}`,
        color: meta?.color ?? "var(--text)",
        borderRadius: 12,
        padding: "2px 8px",
        whiteSpace: "nowrap",
      }}
    >
      Offer {meta?.label.toLowerCase() ?? offer.status}
      {offer.amount != null ? ` · $${offer.amount.toLocaleString()}` : ""}
    </span>
  );
}

export function SecondaryButton({
  onClick,
  children,
  danger,
  disabled,
}: {
  onClick: () => void;
  children: React.ReactNode;
  danger?: boolean;
  disabled?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        background: danger ? "var(--bad)" : "transparent",
        border: danger ? "none" : "1px solid var(--border)",
        color: danger ? "white" : "var(--muted)",
        padding: "6px 12px",
      }}
    >
      {children}
    </button>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span className="muted" style={{ fontSize: 11 }}>
        {label}
      </span>
      {children}
    </label>
  );
}

/** Click a star to set, click the active star again to clear. */
function StarPicker({ value, onChange }: { value: number | null; onChange: (v: number | null) => void }) {
  return (
    <span style={{ display: "inline-flex", gap: 2 }}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(value === n ? null : n)}
          aria-label={`${n} of 5`}
          style={{
            background: "transparent",
            border: "none",
            padding: "0 1px",
            fontSize: 20,
            lineHeight: 1,
            cursor: "pointer",
            color: value != null && n <= value ? "var(--warn)" : "var(--border)",
          }}
        >
          ★
        </button>
      ))}
    </span>
  );
}

export function Stars({ value }: { value: number }) {
  return (
    <span style={{ color: "var(--warn)", letterSpacing: 1 }} aria-label={`${value} of 5`}>
      {"★".repeat(value)}
      <span style={{ color: "var(--border)" }}>{"★".repeat(Math.max(0, 5 - value))}</span>
    </span>
  );
}

export function fmtVisitDate(iso: string): string {
  // Parse as a local date; `new Date("2026-08-05")` is UTC midnight and can
  // render as the previous day west of Greenwich.
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return iso;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}
